import React, { useState } from "react";
import { Button, styled, Box, Typography } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { AUTHORIZE_GA } from "../../gql/queries/ga4";

const GAConnectionWrapper = styled("section")(({ theme }) => ({
  marginBottom: "20px",

  "& .connectionCard": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "6px",
    padding: "24px",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    boxShadow: "none",
  },

  "& h2": {
    fontSize: "20px",
    fontWeight: 700,
    marginBottom: "18px",
    color: theme.palette.text.primary,
  },

  "& .statusBadge": {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "14px",
  },

  "& .connectedBadge": {
    backgroundColor: theme.palette.mode === "dark" ? "#1e4620" : "#d4edda",
    color: theme.palette.mode === "dark" ? "#a3f7b5" : "#155724",
  },

  "& .disconnectedBadge": {
    backgroundColor: theme.palette.mode === "dark" ? "#4a1f24" : "#f8d7da",
    color: theme.palette.mode === "dark" ? "#ffb3b3" : "#721c24",
  },

  "& .statusDot": {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },

  "& .email": {
    fontSize: "13.5px",
    color: theme.palette.text.secondary,
    marginBottom: "14px",
  },

  "& .description": {
    fontSize: "14px",
    color: theme.palette.text.secondary,
    marginBottom: "18px",
    lineHeight: 1.6,
  },

  "& .primaryBtn": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 600,

    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },

    "&.Mui-disabled": {
      opacity: 0.4,
    },
  },

  "& .secondaryBtn": {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 600,
    backgroundColor: theme.palette.error.main,
    color: "#fff",

    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },

  "& .errorBox": {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
    padding: "12px",
    borderRadius: "6px",
    fontSize: "13px",
    marginBottom: "16px",
    lineHeight: 1.5,

    "& code": {
      backgroundColor: theme.palette.action.hover,
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "12px",
    },

    "& a": {
      color: theme.palette.primary.main,
      fontWeight: 600,
      textDecoration: "none",

      "&:hover": {
        textDecoration: "underline",
      },
    },

    "& ol": {
      marginTop: "6px",
      paddingLeft: "18px",
      fontSize: "12px",
    },
  },
}));

function GAConnection({ authStatus, onConnect, onDisconnect }) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const [authorizeGa] = useLazyQuery(AUTHORIZE_GA, {
    fetchPolicy: "no-cache",
  });

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);

    try {
      const { data } = await authorizeGa();
      const authUrl = data?.authorizeGa?.authorization_url;

      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setError("Failed to get authorization URL");
        setConnecting(false);
      }
    } catch (err) {
      console.error("Error initiating GA OAuth:", err);
      setError("Failed to initiate Google Analytics authentication");
      setConnecting(false);
    }
  };

  return (
    <GAConnectionWrapper>
      <Box className="connectionCard">
        <Typography variant="h2">Google Analytics Connection</Typography>

        {authStatus?.authenticated ? (
          <Box>
            <Box className="statusBadge connectedBadge">
              <Box component="span" className="statusDot" />
              Connected
            </Box>

            <Box className="email">{authStatus.email}</Box>

            <Button className="secondaryBtn" onClick={onDisconnect}>
              Disconnect
            </Button>
          </Box>
        ) : (
          <Box className="disconnected">
            <Box className="statusBadge disconnectedBadge">
              <Box component="span" className="statusDot" />
              Not Connected
            </Box>

            <Box className="description">Connect your Google Analytics account to view comprehensive analytics data from GA4.</Box>

            {error && (
              <Box className="errorBox">
                <strong>Error:</strong> {error}
                {error.includes("redirect_uri_mismatch") && (
                  <>
                    <Box mt={1}>
                      <strong>Steps to fix:</strong>
                    </Box>
                    <ol>
                      <li>
                        Go to{" "}
                        <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">
                          Google Cloud Console Credentials
                        </a>
                      </li>
                      <li>Open your OAuth Client ID</li>
                      <li>
                        Add redirect URI:
                        <br />
                        <code>http://localhost:8000/api/auth/ga/callback</code>
                      </li>
                      <li>Save and retry</li>
                    </ol>
                  </>
                )}
              </Box>
            )}

            <Button className="primaryBtn" onClick={handleConnect} disabled={connecting}>
              {connecting ? "Connecting..." : "Connect Google Analytics"}
            </Button>
          </Box>
        )}
      </Box>
    </GAConnectionWrapper>
  );
}

export default GAConnection;
